/**
 * What a material's row should actually claim is usable, and what is being held
 * back by quarantine.
 *
 * The server's `availableQuantity` is `quantity` minus what is HELD — it knows
 * nothing about the gate. So a material with 30 units quarantined and 10 free
 * reported 40 available, sending someone to the shelf for stock the allocator
 * would never offer them (`offeredLots` drops a quarantined lot outright, and
 * the reserve endpoint refuses one with a 400).
 *
 * Derived from the lots when the row carries them, because that is the only
 * place a gate status exists. Without lots there is nothing to reason about and
 * the server's own figure stands — the row is no worse off than before.
 *
 * Pure, and in `src/lib` rather than the table, because `vitest.config.ts`
 * matches `src/**\/*.test.ts` only: logic inside a `.tsx` would never run a test.
 */

/** The fields of a lot this needs. Structural, so `ApiLot` satisfies it. */
export interface StockLot {
  quantity: number
  availableQuantity?: number
  gateStatus?: 'quarantine' | 'in_process' | 'available' | null
}

export interface MaterialStock {
  /** Free to commit to a run right now — quarantined stock excluded. */
  usable: number
  /** On the shelf but gated out. Shown separately; never folded into `usable`. */
  quarantined: number
}

/** A lot's free quantity, preferring the server's figure over the raw count. */
function freeOn(lot: StockLot): number {
  return lot.availableQuantity ?? lot.quantity ?? 0
}

/**
 * Split a material's stock into what can be used and what quarantine is holding.
 *
 * `fallbackAvailable` is the row's own `availableQuantity`, used when the row
 * carries no lots — a list response that omits them must not make every
 * material read as empty.
 */
export function materialStock(
  lots: readonly StockLot[] | undefined,
  fallbackAvailable: number,
): MaterialStock {
  if (!lots || lots.length === 0) return { usable: fallbackAvailable, quarantined: 0 }

  let usable = 0
  let quarantined = 0
  for (const lot of lots) {
    // Only `quarantine` gates stock out. `in_process` is paperwork underway on
    // a receipt that is otherwise fine, and an ungated lot has nothing holding
    // it at all — the same rule the process-step allocator applies.
    if (lot.gateStatus === 'quarantine') quarantined += lot.quantity ?? 0
    else usable += freeOn(lot)
  }
  return { usable, quarantined }
}
